import { getDownloadUrl } from "./media";

type ZipFile = {
  title: string;
  format?: string;
  access?: string;
  sourcePath?: string;
  storageProvider?: string;
  deliveryUrl?: string;
};

function safeName(title: string, format?: string): string {
  const base = title.replace(/[\\/:*?"<>|]+/g, "_").trim() || "file";
  if (/\.[a-z0-9]{2,5}$/i.test(base)) return base;
  const ext = (format || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  return ext ? `${base}.${ext}` : base;
}

/** Download available files in a group as a single ZIP (client-side). */
export async function downloadFilesAsZip(
  files: ZipFile[],
  zipName: string,
  onProgress?: (done: number, total: number) => void,
): Promise<void> {
  const available = files.filter((f) => f.access === "available" && getDownloadUrl(f));
  if (available.length === 0) {
    throw new Error("No downloadable files in this group");
  }

  const JSZip = (await import("jszip")).default;
  const zip = new JSZip();
  let done = 0;

  for (const file of available) {
    const url = getDownloadUrl(file);
    if (!url) continue;
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`Failed to fetch ${file.title} (${res.status})`);
    }
    const blob = await res.blob();
    zip.file(safeName(file.title, file.format), blob);
    done += 1;
    onProgress?.(done, available.length);
  }

  const out = await zip.generateAsync({ type: "blob" });
  const a = document.createElement("a");
  const objectUrl = URL.createObjectURL(out);
  a.href = objectUrl;
  a.download = zipName.endsWith(".zip") ? zipName : `${zipName}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}
